import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { serverTypeShort } from '@/utils/server-type';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { Activity, AlertCircle, Radio, RefreshCw, Users, Zap, Trophy } from 'lucide-react';
import { dashboardService } from '@/services';
import type { DashboardData, DashboardScheduleItem, DashboardLinearChannel } from '@/services';
import toast from 'react-hot-toast';
import { isOnAirCard, isPartialStart, partialBadgeText } from '@/utils/partial-start';

/* ─── Constants ─── */

const POLL_INTERVAL_MS = 30_000; // refresh data every 30s

const LOG_LEVEL_COLOR: Record<string, string> = {
  debug: 'bg-gray-100 text-gray-600',
  info: 'bg-blue-100 text-blue-700',
  warn: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700',
};

/* ─── Helpers ─── */

function formatTime(isoString: string): string {
  try {
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '--:--';
  }
}

function formatTimestamp(isoString: string): string {
  try {
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '--:--:--';
  }
}

function formatUptime(seconds: number): string {
  if (!seconds || seconds <= 0) return '0m';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

/* ─── Helpers for team channels ─── */

function sanitizeTeamName(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9\s_-]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 30);
}

function buildTeamChannelName(prefix: string, teamName: string): string {
  return `${prefix}_${sanitizeTeamName(teamName)}`;
}

/* ─── Live Event Card (grid-based) ─── */

function LiveEventCard({
  event,
  tickOffset,
}: {
  event: DashboardScheduleItem;
  tickOffset: number;
}) {
  const { t } = useTranslation();
  const isRunning = event.xaccel_real_status === 'running';
  const uptime = (event.xaccel_uptime ?? 0) + tickOffset;

  const borderColor = isRunning ? 'border-l-green-500' : 'border-l-yellow-400';
  const bgColor = isRunning ? 'bg-green-50/30' : 'bg-yellow-50';

  // Team channels logic
  const hasTeamChannels = (event.channel_mode === 'team' || event.channel_mode === 'both') &&
    (event.home_team || event.away_team);

  // Build team channel names
  const teamChannelsList: string[] = [];
  if ((event.channel_mode === 'team' || event.channel_mode === 'both') && event.channel_prefix) {
    if (event.home_team) teamChannelsList.push(buildTeamChannelName(event.channel_prefix, event.home_team));
    if (event.away_team) teamChannelsList.push(buildTeamChannelName(event.channel_prefix, event.away_team));
  }

  // Build all channels for tooltip (for 'both': numbered + team channels)
  const allChannelsForTooltip: string[] = [];
  if (event.channel_mode === 'both' && event.xaccel_channel_name) {
    allChannelsForTooltip.push(event.xaccel_channel_name);
  }
  allChannelsForTooltip.push(...teamChannelsList);

  // Per-server status
  const eventServers = event.servers;
  const hasMultiServer = eventServers && eventServers.length > 0;

  return (
    <div className={`border border-gray-200 border-l-[3px] ${borderColor} ${bgColor} rounded-r-lg p-3`}>
      {/* Header: status dot + time + server badges */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full shrink-0 ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-yellow-400'}`} />
          <span className="text-xs font-mono text-dark-400">{formatTime(event.event_start_time)}</span>
          {event.partial_start && isPartialStart(event) && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold leading-none bg-amber-100 text-amber-700" title={t('schedule.statusPartial')}>
              {t('schedule.statusPartial')} {partialBadgeText(event.partial_start)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {hasMultiServer ? (
            eventServers.map(srv => (
              <span
                key={srv.id}
                className={`text-[9px] px-1 py-0.5 rounded font-medium leading-none ${
                  srv.server_type === 'ffstream'
                    ? srv.status === 'running' ? 'bg-purple-100 text-purple-700' : 'bg-purple-50 text-purple-400'
                    : srv.status === 'running' ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-400'
                }`}
                title={`${srv.name}: ${srv.status}`}
              >
                {serverTypeShort(srv.server_type)} {srv.status === 'running' ? '✓' : '⚠'}
              </span>
            ))
          ) : (
            !isRunning && <AlertCircle className="w-3.5 h-3.5 text-yellow-600" />
          )}
        </div>
      </div>

      {/* Event title */}
      <div className="text-sm font-medium text-dark-100 truncate mb-1">
        {event.event_title}
      </div>

      {/* Competition badge + teams icon + channel */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {event.competition_name && (
          <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium truncate max-w-[120px]">
            {event.competition_name}
          </span>
        )}
        {/* Teams icon with custom tooltip for team/both modes */}
        {hasTeamChannels && (
          <div className="relative group">
            <span className="text-dark-400 cursor-help">
              <Users className="w-3 h-3" />
            </span>
            {/* Custom tooltip */}
            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
              <div className="bg-dark-800 text-white text-xs rounded-lg shadow-lg p-2.5 whitespace-nowrap min-w-[140px]">
                {/* Match info */}
                {event.home_team && event.away_team && (
                  <div className="font-medium text-white mb-1.5 pb-1.5 border-b border-dark-600">
                    {event.home_team} vs {event.away_team}
                  </div>
                )}
                {/* Channels list */}
                <div className="text-[10px] text-dark-300 mb-1">Canali:</div>
                <div className="space-y-0.5">
                  {allChannelsForTooltip.map((ch, idx) => (
                    <div key={idx} className="font-mono text-[10px] text-dark-200">
                      {ch}
                    </div>
                  ))}
                </div>
                {/* Arrow */}
                <div className="absolute left-3 bottom-0 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-dark-800" />
              </div>
            </div>
          </div>
        )}
        {/* Channel display: for 'team' mode show both team channels, otherwise show xaccel_channel_name */}
        {event.channel_mode === 'team' && teamChannelsList.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            {teamChannelsList.map((ch, idx) => (
              <span key={idx} className="text-[10px] font-mono text-dark-400">
                {ch}
              </span>
            ))}
          </div>
        ) : event.xaccel_channel_name && (
          <span className="text-[10px] font-mono text-dark-400">
            {event.xaccel_channel_name}
          </span>
        )}
      </div>

      {/* Stats: uptime only */}
      {isRunning && (
        <div className="text-xs text-dark-400">
          <span className="font-mono tabular-nums">{formatUptime(uptime)}</span>
        </div>
      )}
    </div>
  );
}

/* ─── Linear Channel Card (grid-based) ─── */

function LinearChannelCard({
  channel,
  tickOffset,
}: {
  channel: DashboardLinearChannel;
  tickOffset: number;
}) {
  const { t } = useTranslation();
  const isRunning = channel.real_status === 'running';

  // Max uptime from running sub-channels + tickOffset
  const maxUptime = channel.sub_channels
    .filter((sc) => sc.real_status === 'running')
    .reduce((max, sc) => Math.max(max, sc.uptime + tickOffset), 0);

  // Build resolution → servers map for smart display
  const resServerMap = new Map<string, Array<{ server_name: string; server_type: string; running: boolean }>>();
  for (const sc of channel.sub_channels) {
    if (!sc.resolution) continue;
    if (!resServerMap.has(sc.resolution)) resServerMap.set(sc.resolution, []);
    const arr = resServerMap.get(sc.resolution)!;
    // Avoid duplicate server entries for same resolution
    if (!arr.some(s => s.server_name === sc.server_name)) {
      arr.push({
        server_name: sc.server_name || 'Server',
        server_type: sc.server_type || 'xaccel',
        running: sc.real_status === 'running',
      });
    }
  }

  // Unique server types for this channel
  const uniqueServers = new Map<string, { name: string; type: string; running: boolean }>();
  for (const sc of channel.sub_channels) {
    if (sc.server_name && !uniqueServers.has(sc.server_name)) {
      uniqueServers.set(sc.server_name, {
        name: sc.server_name,
        type: sc.server_type || 'xaccel',
        running: sc.real_status === 'running',
      });
    }
  }

  const borderColor = isRunning ? 'border-l-green-500' : 'border-l-gray-300';
  const bgColor = channel.mismatch ? 'bg-yellow-50' : 'bg-gray-50';

  return (
    <div className={`border border-gray-200 border-l-[3px] ${borderColor} ${bgColor} rounded-r-lg p-3`}>
      {/* Row 1: Channel name + status dot + server badges */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`w-2 h-2 rounded-full shrink-0 ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
        <span className="text-sm font-medium text-dark-100 truncate">{channel.name}</span>
        {channel.playback_identity === 'androidtv' && (
          <span title={t('linear.identityTitle')} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-violet-50 text-violet-700 border-violet-200">{t('linear.identityAndroidTv')}</span>
        )}
        {channel.mismatch && (
          <AlertCircle className="w-3.5 h-3.5 text-yellow-600 shrink-0" />
        )}
        {/* Server badges inline */}
        {uniqueServers.size > 0 && (
          <div className="flex gap-0.5 ml-auto shrink-0">
            {Array.from(uniqueServers.values()).map(srv => (
              <span
                key={srv.name}
                className={`text-[9px] px-1 py-0.5 rounded font-medium leading-none ${
                  srv.type === 'ffstream'
                    ? srv.running ? 'bg-purple-100 text-purple-700' : 'bg-purple-50 text-purple-400'
                    : srv.running ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-400'
                }`}
                title={`${srv.name} — ${srv.running ? 'running' : 'stopped'}`}
              >
                {serverTypeShort(srv.type)}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Row 2: Stats or Stopped */}
      {isRunning ? (
        <>
          <div className="flex items-center gap-3 text-xs text-dark-400">
            <span className="font-mono tabular-nums">{formatUptime(maxUptime)}</span>
          </div>
          {/* Resolution pills — deduplicated, with per-server dots */}
          {resServerMap.size > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {Array.from(resServerMap.entries()).map(([res, servers]) => (
                <span
                  key={res}
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium"
                  title={servers.map(s => `${s.server_name}: ${s.running ? 'running' : 'stopped'}`).join(', ')}
                >
                  {res}
                  {servers.length > 1 && (
                    <span className="flex gap-px ml-0.5">
                      {servers.map(s => (
                        <span
                          key={s.server_name}
                          className={`w-1.5 h-1.5 rounded-full ${
                            s.running
                              ? s.server_type === 'ffstream' ? 'bg-purple-600' : 'bg-blue-600'
                              : 'bg-gray-400'
                          }`}
                        />
                      ))}
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
        </>
      ) : (
        <span className="text-xs text-dark-400">{t('linear.stopped')}</span>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Main component — reactive with auto-refresh
   + client-side uptime ticker
   ═══════════════════════════════════════════════ */

export default function UserDashboard() {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  // Data state
  const [data, setData] = useState<DashboardData | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Uptime tick offset — increments every second, resets on each API fetch
  const [tickOffset, setTickOffset] = useState(0);
  const fetchingRef = useRef(false);

  // Fetch data (silent after first load)
  const fetchData = useCallback(async (silent: boolean) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (!silent) setRefreshing(true);

    try {
      const result = await dashboardService.getStats();
      setData(result);
      setTickOffset(0); // reset uptime ticker
    } catch {
      if (!silent) toast.error(t('common.error'));
    } finally {
      fetchingRef.current = false;
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  // Initial fetch
  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  // Auto-refresh polling every 30s
  useEffect(() => {
    const id = setInterval(() => fetchData(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  // Uptime ticker — 1 second interval
  useEffect(() => {
    const id = setInterval(() => setTickOffset(prev => prev + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Filter live events for cards display
  const liveEvents = useMemo(() => {
    if (!data?.todaySchedule) return [];
    return data.todaySchedule.filter(isOnAirCard);
  }, [data?.todaySchedule]);

  /* Loading skeleton — only on first load */
  if (initialLoading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-64 bg-gray-100 rounded animate-pulse mt-2" />
        </div>
        {/* Schedule hero skeleton */}
        <div className="card animate-pulse bg-gray-50">
          <div className="h-6 w-56 bg-gray-200 rounded mb-4" />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-5 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
        {/* Linear skeleton */}
        <div className="card h-36 animate-pulse bg-gray-50" />
        {/* 2-column overview+logs skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card h-32 animate-pulse bg-gray-50" />
          <div className="card h-32 animate-pulse bg-gray-50" />
        </div>
      </div>
    );
  }

  const stats = data?.stats ?? { live: 0, scheduled: 0, completed: 0, errors: 0 };
  const overview = data?.overview;
  const linearChannels = data?.linearChannels ?? [];
  const runningLinear = linearChannels.filter(ch => ch.real_status === 'running').length;

  return (
    <div className="space-y-6">
      {/* Header + refresh indicator */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('dashboard.title')}</h1>
          <p className="text-dark-400 mt-1">
            {t('dashboard.welcome')}, {user?.username}
          </p>
        </div>
        <button
          onClick={() => fetchData(false)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-xs text-dark-400 hover:text-dark-200 transition-colors mt-1"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">
            {refreshing ? t('common.loading') : `${Math.min(tickOffset, POLL_INTERVAL_MS / 1000)}s`}
          </span>
        </button>
      </div>

      {/* ─── Today Schedule + Live Events ─── */}
      <div className="card">
        {/* Header with title + stats */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Zap className={`w-5 h-5 ${stats.live > 0 ? 'text-green-500' : 'text-dark-300'}`} />
            <h2 className="text-lg font-semibold text-dark-100">
              {t('dashboard.liveNow')}
            </h2>
          </div>
          {/* Stats pills */}
          <div className="flex items-center gap-3 text-xs">
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${stats.live > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              <span className={`w-2 h-2 rounded-full ${stats.live > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <span className="font-semibold">{stats.live}</span>
              <span>Live</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-blue-50 text-blue-600">
              <span className="w-2 h-2 rounded-full bg-blue-400" />
              <span className="font-semibold">{stats.scheduled}</span>
              <span>{t('dashboard.scheduledShort')}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-100 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              <span className="font-semibold">{stats.completed}</span>
              <span>{t('dashboard.completedShort')}</span>
            </span>
            {stats.errors > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-100 text-red-600">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="font-semibold">{stats.errors}</span>
                <span>{t('dashboard.errorsShort')}</span>
              </span>
            )}
          </div>
        </div>

        {/* Live Events Grid */}
        {liveEvents.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {liveEvents.map((ev) => (
              <LiveEventCard key={ev.id} event={ev} tickOffset={tickOffset} />
            ))}
          </div>
        ) : (
          <p className="text-dark-400 text-sm">{t('dashboard.noLiveEvents')}</p>
        )}
      </div>

      {/* ─── Linear Channels — Card Grid ─── */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Radio className="w-5 h-5 text-dark-300" />
          <h2 className="text-lg font-semibold text-dark-100">
            {t('dashboard.linearChannelsPanel')}
          </h2>
          <span className="text-xs text-dark-400 bg-gray-100 px-2 py-0.5 rounded-full font-medium">
            {runningLinear}/{linearChannels.length}
          </span>
        </div>
        {runningLinear > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {linearChannels
              .filter((ch) => ch.real_status === 'running')
              .map((ch) => (
                <LinearChannelCard key={ch.id} channel={ch} tickOffset={tickOffset} />
              ))}
          </div>
        ) : (
          <p className="text-dark-400 text-sm">{t('dashboard.noLinearChannels')}</p>
        )}
      </div>

      {/* ─── Two columns — Overview + Recent Activity ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Overview — Simplified */}
        <div className="card">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">
            {t('dashboard.quickOverview')}
          </h2>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-dark-400 flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                {t('dashboard.configuredSports')}
              </span>
              <span className="text-dark-100 font-medium">{overview?.sportsCount ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-dark-400 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                {t('dashboard.activeCompetitions')}
              </span>
              <span className="text-dark-100 font-medium">{overview?.activeCompetitions ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-dark-400 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                {t('dashboard.daznAccount')}
              </span>
              {overview?.daznSubscriptionStatus ? (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                  Active
                </span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500">
                  {t('dashboard.notConnected')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">
            {t('dashboard.recentActivity')}
          </h2>
          {data?.recentLogs && data.recentLogs.length > 0 ? (
            <div className="space-y-2">
              {data.recentLogs.map((log) => {
                const levelColor = LOG_LEVEL_COLOR[log.level] ?? LOG_LEVEL_COLOR.info;
                return (
                  <div key={log.id} className="flex items-start gap-3 text-sm">
                    <span className="text-dark-400 font-mono text-xs w-16 shrink-0 pt-0.5">
                      {formatTimestamp(log.created_at)}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 ${levelColor}`}>
                      {log.level}
                    </span>
                    <span className="text-dark-100 truncate">{log.message}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-dark-400 text-sm">{t('common.noData')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
